<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/tooltip'
import AssessmentGradeChips from '@/assessments/AssessmentGradeChips.vue'
import { fileUploadErrorFor } from '@/assessments/assessment_wizard_query'
import { GRADES } from '@/browse/grades'
import { gradeLabel } from '@/util/gradesort'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { EVENT_NAMES } from '@/tracking/analytics/event_names'
import { announce } from '@/util/live_announcer'
import uploadedFileIcon from '@/assets/assessments/uploaded_file.svg'

const MAX_TEXTAREA_HEIGHT = 200
const QUERY_ERROR_ID = 'query-error'
const FILE_UPLOAD_ERROR_ID = 'file-upload-error'
const GRADE_ACCORDION_ID = 'grade-accordion-content'
const GRADES_LABEL_ID = 'assessment-wizard-grades-label'
const FILE_PRIVACY_NOTE = 'Your file will not be stored. Files are processed in real time and immediately discarded.'

const query = defineModel<string>('query', { required: true })
const files = defineModel<File[]>('files', { required: true })
const gradeLevels = defineModel<string[]>('gradeLevels', { required: true })
const isGradeAccordionOpen = defineModel<boolean>('gradeAccordionOpen', { required: true })

const emit = defineEmits<{ submit: [] }>()

const analytics = getAnalyticsTracker()

const queryTextarea = ref<HTMLTextAreaElement | null>(null)
const fileInput = ref<HTMLInputElement | null>(null)
const hasAttemptedSubmit = ref(false)
const fileUploadError = ref('')
const hasViewedQueryTooltip = ref(false)

const isQueryBlank = computed(() => query.value.trim().length === 0)
const showsQueryError = computed(() => hasAttemptedSubmit.value && isQueryBlank.value)
const uploadedFile = computed(() => files.value[0])

const gradeLevelSummary = computed(() => {
  if (!gradeLevels.value.length) {
    return 'Searching all grade levels'
  }
  return `Searching ${gradeLevels.value.map(gradeLabel).join(', ')}`
})

function fitTextareaToQuery() {
  const textarea = queryTextarea.value
  if (!textarea) {
    return
  }
  // Reset first, or scrollHeight reports the current height and the box never shrinks.
  textarea.style.height = 'auto'
  textarea.style.height = `${Math.min(textarea.scrollHeight, MAX_TEXTAREA_HEIGHT)}px`
}

function queryTyped() {
  fitTextareaToQuery()
  fileUploadError.value = ''
}

function chooseFile(event: Event) {
  const input = event.target as HTMLInputElement
  const [file] = Array.from(input.files ?? [])
  // Cleared every time, so choosing the same file again after removing it still fires change.
  input.value = ''
  fileUploadError.value = ''
  if (!file) {
    return
  }
  const error = fileUploadErrorFor(file)
  if (error) {
    fileUploadError.value = error
    return
  }
  files.value = [file]
  announce(`Privacy note: ${FILE_PRIVACY_NOTE}`)
}

function removeFile() {
  files.value = []
  fileUploadError.value = ''
  hasAttemptedSubmit.value = false
}

function queryTooltipOpenChanged(isOpen: boolean) {
  if (isOpen && !hasViewedQueryTooltip.value) {
    analytics.sendEvent(EVENT_NAMES.ASSESSMENT_WIZARD.VIEWED_QUERY_TOOLTIP)
    hasViewedQueryTooltip.value = true
  }
}

function submit() {
  hasAttemptedSubmit.value = true
  if (isQueryBlank.value) {
    return
  }
  hasAttemptedSubmit.value = false
  fileUploadError.value = ''
  emit('submit')
}

watch(query, (newQuery) => {
  if (hasAttemptedSubmit.value && newQuery.trim().length > 0) {
    hasAttemptedSubmit.value = false
  }
})

defineExpose({ focusQuery: () => queryTextarea.value?.focus() })

// A query restored from the last search can be longer than the empty box.
onMounted(async () => {
  await nextTick()
  fitTextareaToQuery()
})
</script>

<template>
  <form class="flex w-full flex-col items-start gap-4 self-stretch" novalidate @submit.prevent="submit">
    <div class="flex w-full flex-col items-start gap-2">
      <label for="assessment-query" class="m-0 flex items-center gap-1.5 text-display-sm font-regular leading-9 text-gray-1000">
        Describe the assessment you need
        <TooltipProvider :delay-duration="0">
          <Tooltip @update:open="queryTooltipOpenChanged">
            <TooltipTrigger as-child>
              <button
                type="button"
                class="flex rounded-full text-gray-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-400"
                aria-label="Tips for describing your assessment"
              >
                <span class="material-icons-round cursor-help text-[18px] leading-[18px]" aria-hidden="true">info_outline</span>
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" class="max-w-[280px] rounded-xs bg-black px-[14px] py-[9px] text-sm leading-[17px] text-white">
              <p class="m-0 mb-1"><strong>Not sure what to type?</strong></p>
              <p class="m-0 mb-1">Describe what your student is working on:</p>
              <ul class="m-0 mt-1 list-disc pl-4">
                <li class="mb-0.5">"My student isn't getting 2-step word problems"</li>
                <li class="mb-0.5">"Oral reading fluency"</li>
                <li class="mb-0.5">Paste in a goal you've written</li>
              </ul>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </label>

      <div
        class="flex max-h-[200px] min-h-[140px] w-full flex-col overflow-hidden rounded-xs border bg-white pb-3 pl-3 pr-[14px] pt-3 focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-blue-400"
        :class="showsQueryError ? 'border-red-300' : 'border-gray-600 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] focus-within:border-blue-400'"
      >
        <textarea
          id="assessment-query"
          ref="queryTextarea"
          v-model="query"
          rows="3"
          class="max-h-[120px] min-h-[84px] w-full flex-auto resize-none overflow-y-auto border-none bg-white p-0 text-xl leading-[18px] text-gray-900 placeholder:leading-[27px] placeholder:text-gray-700 focus:outline-none"
          placeholder="Example: My student needs help finding the main idea in a story"
          aria-label="Describe the assessment you need"
          aria-required="true"
          :aria-invalid="showsQueryError"
          :aria-describedby="showsQueryError ? QUERY_ERROR_ID : undefined"
          @input="queryTyped"
        />

        <div class="flex flex-col items-start self-stretch bg-white pt-4" :class="{ 'border-t border-[#e0e0e0]': !uploadedFile }">
          <input
            ref="fileInput"
            type="file"
            class="hidden"
            accept=".pdf,.jpg,.jpeg,.png"
            aria-label="Upload a file (PDF, JPG, or PNG, max 10MB)"
            :aria-describedby="fileUploadError ? FILE_UPLOAD_ERROR_ID : undefined"
            :aria-invalid="Boolean(fileUploadError)"
            @change="chooseFile"
          >

          <div v-if="!uploadedFile" class="flex w-full items-center gap-2.5">
            <button
              type="button"
              class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#f0f0f0] px-[7px] transition-colors hover:bg-gray-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-400"
              aria-label="Upload a file"
              :aria-describedby="fileUploadError ? FILE_UPLOAD_ERROR_ID : undefined"
              @click="fileInput?.click()"
            >
              <span class="material-icons text-[14px] leading-[14px] text-[#6E6E6E]" aria-hidden="true">add</span>
            </button>
            <div class="flex flex-1 flex-col">
              <p class="m-0 flex items-center gap-1 p-0 text-md leading-[18px] text-[#6E6E6E]">
                <span>Upload a file</span>
                <span class="text-sm"> (optional)</span>
                <TooltipProvider :delay-duration="0">
                  <Tooltip>
                    <TooltipTrigger as-child>
                      <button
                        type="button"
                        class="mt-0.5 flex h-[18px] items-center rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-400"
                        aria-label="File privacy information"
                      >
                        <span class="material-icons-round cursor-help text-sm leading-3 text-[#6E6E6E]" aria-hidden="true">info_outline</span>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" class="max-w-[200px] rounded-xs bg-black px-2 py-[3px] text-center text-sm text-white">
                      Your file will not be stored permanently. Files are processed in real time and immediately discarded.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </p>
              <p class="m-0 p-0 text-sm leading-4 text-[#6E6E6E]">
                Attach a score report (e.g. i-Ready) or student work sample for better results.
              </p>
            </div>
          </div>

          <div v-else class="flex max-w-full items-center gap-[13px] rounded-[99px] bg-gray-200 px-3 py-1.5">
            <div class="flex min-w-0 flex-1 items-center gap-2">
              <img :src="uploadedFileIcon" alt="" class="h-3 w-3 shrink-0">
              <span class="truncate text-sm leading-[18px] text-gray-900">{{ uploadedFile.name }}</span>
            </div>
            <button
              type="button"
              class="flex h-4 w-4 shrink-0 items-center justify-center rounded-full p-2 hover:bg-gray-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-400"
              aria-label="Remove file"
              @click="removeFile"
            >
              <span class="material-icons text-lg leading-4 text-gray-900" aria-hidden="true">close</span>
            </button>
          </div>
        </div>
      </div>

      <div v-show="showsQueryError" class="-mt-1 flex items-center gap-3 px-4 text-md font-medium leading-6 text-red-300">
        <span class="material-icons-outlined shrink-0 text-[20px] leading-none" aria-hidden="true">report_problem</span>
        <span :id="QUERY_ERROR_ID" role="alert" aria-live="assertive">
          <template v-if="showsQueryError"><span class="sr-only">Error: </span>Assessment description is required</template>
        </span>
      </div>
      <div v-show="fileUploadError" class="flex items-center gap-3 rounded-xs px-4 leading-6">
        <span class="material-icons-outlined shrink-0 text-[20px] leading-none text-red-300" aria-hidden="true">warning_amber</span>
        <span :id="FILE_UPLOAD_ERROR_ID" class="text-md font-medium leading-5 text-red-300" role="alert" aria-live="assertive">
          <template v-if="fileUploadError"><span class="sr-only">Warning: </span>{{ fileUploadError }}</template>
        </span>
      </div>
      <div v-if="uploadedFile" class="flex w-full items-center gap-1 rounded-xs border border-green-300 bg-[#f0fdf4] px-2.5 py-2">
        <span class="material-icons-outlined shrink-0 text-lg leading-4 text-green-300" aria-hidden="true">shield</span>
        <span class="text-sm leading-4 text-green-300"><span class="sr-only">Privacy note: </span>{{ FILE_PRIVACY_NOTE }}</span>
      </div>
    </div>

    <div class="w-full overflow-hidden rounded-xs border border-gray-700 bg-white" data-testid="grade-accordion">
      <button
        type="button"
        class="flex w-full items-center justify-between bg-white p-3 text-left hover:bg-[#f9f9f9] focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-blue-400"
        :aria-expanded="isGradeAccordionOpen"
        :aria-controls="GRADE_ACCORDION_ID"
        @click="isGradeAccordionOpen = !isGradeAccordionOpen"
      >
        <span class="text-md leading-5 text-gray-1000">
          <strong :id="GRADES_LABEL_ID" class="font-bold">Search by grade levels</strong>
          <span class="text-sm font-regular leading-[1.42857]"> (optional)</span>
        </span>
        <span
          class="material-icons shrink-0 text-display-sm leading-6 text-gray-1000 transition-transform"
          :class="{ 'rotate-180': isGradeAccordionOpen }"
          aria-hidden="true"
        >expand_more</span>
      </button>
      <div v-show="isGradeAccordionOpen" :id="GRADE_ACCORDION_ID" class="px-3 pb-3">
        <AssessmentGradeChips v-model:selected="gradeLevels" :grades="GRADES" :labelled-by="GRADES_LABEL_ID" />
      </div>
    </div>

    <div class="flex min-h-10 items-center gap-3 self-stretch pt-2">
      <button
        type="submit"
        class="inline-flex h-8 items-center whitespace-nowrap rounded-xs bg-blue-400 px-4 py-2 text-md leading-4 text-white hover:bg-[#006ba3] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-400"
        aria-label="Find assessments based on your description"
      >
        Find Assessments
      </button>
      <span class="text-md leading-5 text-gray-700">{{ gradeLevelSummary }}</span>
    </div>
  </form>
</template>
